import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { clamp } from 'src/replicant/utils';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import Scroll from '../components/shared/Scroll';
import {
  DecorationRoomID,
  DecorationThemeID,
} from 'src/replicant/decoration/decoration.ruleset';
import CatalogItemView from './CatalogItemView';
import type { CatalogItem } from 'src/replicant/catalog/catalog.ruleset';
import { getCatalogItems } from './helpers';
import { sellCatalogItem } from './catalog.sequence';
import { AccessoryThemeID } from 'src/replicant/data/AccessoryItems.schema';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = () => void;
type Sorting = 'desc' | 'asc';
type Room = DecorationRoomID;
type CatalogGroup = {
  theme: DecorationThemeID | AccessoryThemeID;
  items: CatalogItem[];
  name: string;
};

// Sort predefined functions, we are sorting strings here
// https://stackoverflow.com/questions/51165/how-to-sort-strings-in-javascript
// If JS natural string compare will fail, we should give a try with localeCompare
const sortFunctions: Record<
  Sorting,
  (
    a: DecorationThemeID | AccessoryThemeID,
    b: DecorationThemeID | AccessoryThemeID,
  ) => number
> = {
  asc: (a, b) => +(a > b) || -(a < b),
  desc: (a, b) => -(a > b) || +(a < b),
};

type Tab = Room | 'accessory';

/**
 * Catalog items list
 */
export default class PopupCatalogList extends PopupBasic {
  private buttonBack: ButtonScaleViewWithText;
  private close: CloseHandler;
  private switch: {
    getCurrentID: () => Tab;
    updateDataIndex: (value: number) => void;
  };

  // Sorting data
  private sort: ButtonScaleView;
  private sorting: Sorting = 'desc';

  // Room select data
  private tabs: Tab[] = ['living', 'toilet', 'accessory'];
  private list: Scroll<CatalogGroup>;

  // Data
  private data: CatalogItem[] = [];

  // No items view
  private noItemsLabel: LangBitmapFontTextView;

  /**
   * Constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      width: 563,
      height: 872,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEMS_LIST._,
      },
      titleIcon: {
        width: 57,
        height: 55,
        image: 'assets/ui/catalog/icons/inventory_icon.png',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    // Room select
    this.switch = this.createRoomSwitch(
      {
        superview: this.box,
        width: 305,
        height: 52,
        x: 25,
        y: 92,
        buttons: {
          width: 57,
          height: 55,
          tracking: {
            feature: Feature.CATALOG._,
            $subFeature: Feature.CATALOG.POPUP_ITEMS_LIST.SWITCH_ROOM,
          },
        },
        text: {
          color: '#ffffff',
          size: 23,
        },
      },
      this.tabs,
      (id) => {
        this.setData(this.data, id);
        if (id === 'accessory') {
          return i18n('accessory.accessories');
        }
        return i18n(`decoration.rooms.${id}`);
      },
    );

    // Sort switch
    this.sort = new ButtonScaleViewWithText({
      superview: this.box,
      x: 387,
      y: 92,
      width: 147,
      height: 53,
      fontSize: 16,
      image: 'assets/ui/shared/buttons/btn_sort.png',
      imagePressed: 'assets/ui/shared/buttons/btn_sort.png',
      imageDisabled: 'assets/ui/shared/buttons/btn_sort.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 76, right: 76 },
      },
      icon: {
        image: 'assets/ui/catalog/icons/sort_descending.png',
        width: 24,
        height: 20,
        offset: {
          x: 40,
          y: 0,
        },
      },
      pressedOffsetY: 2,
      labelOffsetY: 0,
      labelOffsetX: 10,
      textColor: '#9A7171',
      labelHorizontalAlign: 'left',
      font: bitmapFonts('Body'),
      localeText: () => i18n('catalog.popupList.sort'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.switchSort();
      },
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEMS_LIST.SORT,
      },
    });

    // Scroll view
    this.list = new Scroll<CatalogGroup>({
      superview: this.box,
      createItem: (...args) => this.createGroup(...args),
      showBg: true,
      horizontal: false,
      firstItemOffset: 5,
      margin: 10,
      rect: {
        x: (this.box.style.width - 516) * 0.5,
        y: 155,
        width: 516,
        height: 574,
      },
    });

    this.noItemsLabel = new LangBitmapFontTextView({
      superview: this.box,
      centerOnOrigin: true,
      width: this.box.style.width,
      height: this.box.style.height,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2,
      align: 'center',
      verticalAlign: 'center',
      size: 23,
      color: '#BF8147',
      localeText: () => i18n('catalog.popupList.noItems'),
      font: bitmapFonts('Body'),
      canHandleEvents: false,
    });

    // Close button
    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: (563 - 228) * 0.5,
      y: 752,
      width: 228,
      height: 94.7,
      localeText: () => i18n('catalog.popupList.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEMS_LIST.BACK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/catalog/icons/inventory_icon.png',
      'assets/ui/shared/buttons/btn_sort.png',
      'assets/ui/catalog/icons/sort_descending.png',
      'assets/ui/catalog/frames/cell_pressed.png',
      'assets/ui/catalog/icons/sort_ascending.png',
      'assets/ui/shared/frames/quantity_textfield.png',
      'assets/ui/shared/buttons/arrow.png',
      'assets/ui/shared/buttons/arrow_pressed.png',
      'assets/ui/shared/buttons/arrow_disabled.png',
    ]);
  }

  /**
   * On dialog init
   */
  public init(opts: { items: CatalogItem[] }) {
    super.init({
      title: i18n('catalog.popupList.title'),
    });

    this.data = opts.items;

    // Update items name
    this.switch.updateDataIndex(0);

    // Set data
    this.setData(this.data, this.switch.getCurrentID());
  }

  /**
   * Update data
   */
  private setData(data: CatalogItem[], tab: Tab) {
    // Filter rooms
    const items = data.filter(
      (item) =>
        (item.type === 'decoration' && tab === item.room) ||
        (item.type === 'accessory' && tab === 'accessory'),
    );

    // Get all groups
    const groups = items.reduce((acc, value) => {
      acc.add(value.theme);
      return acc;
    }, new Set<DecorationThemeID | AccessoryThemeID>());

    // Sort groups
    const sortedGroups = Array.from(groups).sort((a, b) =>
      sortFunctions[this.sorting](a, b),
    );

    // Grouped data with items and sort by rarity
    const listItems = sortedGroups.map<CatalogGroup>((theme) => {
      return {
        theme,
        name:
          tab === 'accessory'
            ? i18n(`accessory.themes.${theme}`)
            : i18n(`decoration.themes.${theme}`),
        items: items
          .filter((item) => item.theme === theme)
          .sort((a, b) => b.tier - a.tier || b.maxTier - a.maxTier),
      };
    });

    // Hide or show no items label
    this.noItemsLabel.style.visible = !listItems.length;

    // Set list
    this.list?.setItems(listItems);
  }

  /**
   * Item view constructor
   */
  private createGroup(superview: View, _: number, group: CatalogGroup) {
    const padding = { y: 70 };
    const itemSize = { width: 156, height: 195 };
    const width = 512;
    const height = Math.ceil(group.items.length / 3) * itemSize.height;

    const view = new View({
      superview,
      width,
      height: height + padding.y + 35,
      x: 15,
      y: 0,
    });

    const themeName = new LangBitmapFontTextView({
      superview: view,
      height: padding.y,
      localeText: () => group.name,
      font: bitmapFonts('Body'),
      align: 'left',
      verticalAlign: 'center',
      color: '#BF8147',
      size: 26,
    });

    const themeNameWidth = themeName.getTextWidth();
    themeName.updateOpts({ width: themeNameWidth });

    new View({
      superview: view,
      width: width - themeNameWidth - 50,
      height: 2,
      backgroundColor: '#BF8147',
      y: padding.y * 0.5,
      x: themeNameWidth + 20,
    });

    const flexView = new FlexBoxView({
      superview: view,
      y: padding.y,
      width,
      height,
      flex: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        flexWrap: 'wrap',
      },
    });

    for (const item of group.items) {
      const wrap = new View({
        superview: flexView,
        ...itemSize,
        width: itemSize.width + 10,
        height: itemSize.height + 10,
      });

      const itemView = new CatalogItemView({
        superview: wrap,
        imagePressed: 'assets/ui/catalog/frames/cell_pressed.png',
        tracking: {
          feature: Feature.CATALOG._,
          $subFeature: Feature.CATALOG.POPUP_ITEMS_LIST.SELL_ITEM_CLICK,
        },
        pressedOffsetY: 3,
        onClick: () => this.sellItem(item),
      });

      itemView.setItem(item);
    }

    return view;
  }

  /**
   * Sell item action
   */
  private async sellItem(item: CatalogItem) {
    sounds.sfx.play('ok');

    // Action was done?
    const result = await sellCatalogItem(item);

    // Update catalog
    if (result) {
      this.data = getCatalogItems();
      this.setData(this.data, this.switch.getCurrentID());
    }
  }

  /**
   * Switch sorting order
   */
  private switchSort() {
    // Change sorting order
    switch (this.sorting) {
      case 'asc':
        this.sorting = 'desc';
        break;
      case 'desc':
        this.sorting = 'asc';
        break;
    }

    // Update icon
    this.changeSortIcon();

    // Update data
    this.setData(this.data, this.switch.getCurrentID());
  }

  /**
   * Change icon
   */
  private changeSortIcon() {
    switch (this.sorting) {
      case 'asc':
        this.sort.setIcon('assets/ui/catalog/icons/sort_ascending.png');
        break;
      case 'desc':
        this.sort.setIcon('assets/ui/catalog/icons/sort_descending.png');
        break;
    }
  }

  /**
   * Create room switch button
   */
  private createRoomSwitch<T extends string>(
    opts: ViewOpts & {
      buttons: ButtonScaleViewOpts;
      text: LangBitmapOpts;
    },
    data: T[],
    value: (id: T) => string,
  ) {
    const width = opts.width;
    const height = opts.height;
    const buttons = opts.buttons;
    const text = opts.text;

    // Current closure index
    let index = 0;

    // Base view
    const view = new View({
      ...opts,
    });

    // Background image
    new ImageScaleView({
      superview: view,
      width,
      height,
      image: 'assets/ui/shared/frames/quantity_textfield.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
      },
    });

    // Flex layout
    const layout = new FlexBoxView({
      superview: view,
      width,
      height,
      flex: {
        alignItems: 'center',
      },
    });

    // Decrease  button
    const decButton = new ButtonScaleView({
      superview: layout,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      imageDisabled: 'assets/ui/shared/buttons/arrow_disabled.png',
      centerAnchor: true,
      scale: 1.1,
      ...buttons,
      onClick: async () => {
        sounds.playSound('ok');

        updateDataIndex(-1);
      },
    });

    const valueLabel = new LangBitmapFontTextView({
      width: opts.width! - buttons.width! * 2,
      height,
      superview: layout,
      font: bitmapFonts('Body'),
      align: 'center',
      verticalAlign: 'center',
      ...text,
    });

    // Increase button
    const incButton = new ButtonScaleView({
      superview: layout,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      imageDisabled: 'assets/ui/shared/buttons/arrow_disabled.png',
      centerAnchor: true,
      scale: 1.1,
      flipX: true,
      ...buttons,
      onClick: async () => {
        sounds.playSound('ok');

        updateDataIndex(1);
      },
    });

    // Update data index ID and text value
    const updateDataIndex = (inc: number) => {
      // Max index
      const max = data.length - 1;

      // Increase or decrease index
      index = index + inc;
      index = clamp(index, 0, max);

      // Update text value
      valueLabel.localeText = () => value(data[index]);
    };

    // Return public api
    return {
      updateDataIndex,
      getCurrentID: () => data[index],
    };
  }
}
